import { relataHoras } from '@sheafyield/dominio';
import type { CSSProperties } from 'react';

import { Anel } from '../ds/Anel.js';
import { duracaoPorExtenso, formatarDuracao } from '../hoje/relogio.js';
import type { VisaoDoRelatorio } from './dados.js';

export interface PropsResumoDoMes {
  visao: VisaoDoRelatorio;
}

const nomeDoMes = (ano: number, mes: number) =>
  new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric' }).format(
    new Date(ano, mes - 1, 1),
  );
const ROTULOS = {
  publicador: 'Publicador',
  nao_batizado: 'Não batizado',
  auxiliar: 'Pioneiro auxiliar',
  regular: 'Pioneiro regular',
  especial: 'Pioneiro especial',
  missionario: 'Missionário',
} as const;

function Semana({
  semana,
  indice,
}: {
  semana: PropsResumoDoMes['visao']['semanas'][number];
  indice: number;
}) {
  const de = new Date(semana.de).toLocaleDateString('pt-BR');
  const ate = new Date(new Date(semana.ate).getTime() - 1).toLocaleDateString(
    'pt-BR',
  );
  return (
    <li>
      <span>
        Semana {indice + 1}: {de} a {ate}
      </span>
      <strong>{formatarDuracao(Math.floor(semana.campoMs / 60_000))}</strong>
    </li>
  );
}

export function ResumoDoMes({ visao }: PropsResumoDoMes) {
  const { atual, perfil } = visao;
  const comHoras = relataHoras(perfil.tipo);
  const campoMs = visao.semanas.reduce(
    (total, semana) => total + semana.campoMs,
    0,
  );
  const minutosRegistrados = Math.floor(campoMs / 60_000);
  const brutoDisponivel = visao.baseCompleta;
  const maximo = Math.max(...visao.semanas.map((semana) => semana.campoMs), 1);
  return (
    <section
      className="relatorio__conteudo"
      aria-label={`Resumo de ${nomeDoMes(atual.ano, atual.mes)}`}
    >
      <p className="relatorio__periodo">{nomeDoMes(atual.ano, atual.mes)}</p>
      <p className="relatorio__perfil">{ROTULOS[perfil.tipo]}</p>
      {!visao.perfilPersistido ? <a href="/ajustes">Perfil e meta</a> : null}
      {!visao.baseCompleta ? (
        <p className="relatorio__aviso" role="status">
          A apuração ainda pode mudar enquanto o histórico é sincronizado.
        </p>
      ) : null}
      {comHoras ? (
        <div className="relatorio__resumo">
          <div className="relatorio__anel">
            {brutoDisponivel ? (
              <Anel
                valor={minutosRegistrados}
                total={perfil.meta_horas_mes * 60}
                rotulo="minutos de horas de campo"
              />
            ) : null}
            {brutoDisponivel ? (
              <p>
                Horas de campo: {duracaoPorExtenso(minutosRegistrados)} de{' '}
                {duracaoPorExtenso(perfil.meta_horas_mes * 60)}.
              </p>
            ) : null}
          </div>
          <p
            aria-label={`${brutoDisponivel ? 'Horas de campo' : 'Horas do relatório'}: ${duracaoPorExtenso(brutoDisponivel ? minutosRegistrados : atual.horas_minutos)} de ${duracaoPorExtenso(perfil.meta_horas_mes * 60)}`}
          >
            <strong>
              {brutoDisponivel
                ? formatarDuracao(minutosRegistrados)
                : formatarDuracao(atual.horas_minutos)}
            </strong>
            <br />
            de {formatarDuracao(perfil.meta_horas_mes * 60)}
            {brutoDisponivel
              ? ` · ${Math.round((minutosRegistrados / Math.max(1, perfil.meta_horas_mes * 60)) * 100)}% da meta`
              : null}
          </p>
        </div>
      ) : (
        <p className="relatorio__participacao">
          <strong>
            {atual.participou ? 'Participei' : 'Ainda não participei'}
          </strong>
          <br />
          Atividade registrada em {nomeDoMes(atual.ano, atual.mes)}
        </p>
      )}
      {!brutoDisponivel && comHoras ? (
        <p>Tempo registrado indisponível até sincronizar.</p>
      ) : null}
      <dl className="relatorio__totais">
        {comHoras ? (
          <>
            <dt>Horas do relatório</dt>
            <dd>{formatarDuracao(atual.horas_minutos)}</dd>
          </>
        ) : null}
        {comHoras ? (
          <>
            <dt>Créditos</dt>
            <dd>{formatarDuracao(atual.creditos_minutos)}</dd>
          </>
        ) : null}
        <dt>Estudos bíblicos</dt>
        <dd>{atual.estudos}</dd>
        {comHoras && atual.transporte_minutos > 0 ? (
          <>
            <dt>Transporte</dt>
            <dd>{formatarDuracao(atual.transporte_minutos)} transportados</dd>
          </>
        ) : null}
      </dl>
      {atual.sobrescrito ? (
        <p className="relatorio__aviso">Ajustado manualmente</p>
      ) : null}
      {atual.enviado_em ? (
        <p className="relatorio__aviso">
          Marcado como enviado em{' '}
          {new Date(atual.enviado_em).toLocaleDateString('pt-BR')}
        </p>
      ) : null}
      {comHoras ? (
        <>
          {brutoDisponivel ? (
            <div className="relatorio__grafico" aria-hidden="true">
              {visao.semanas.map((semana, indice) => (
                <span
                  key={semana.de}
                  className="relatorio__barra"
                  style={
                    {
                      '--altura': `${Math.round((semana.campoMs / maximo) * 100)}%`,
                    } as CSSProperties
                  }
                >
                  S{indice + 1}
                </span>
              ))}
            </div>
          ) : null}
          <ul
            className="relatorio__semanas"
            aria-label="Tempo registrado por semana"
          >
            {visao.semanas.map((semana, indice) => (
              <Semana key={semana.de} semana={semana} indice={indice} />
            ))}
          </ul>
        </>
      ) : null}
    </section>
  );
}
